/* ==========================================================================
   PDP quantity breaks — buy 2 save 10%, buy 3 save 15%.
   Loaded only by snippets/product-block-quantity-breaks.liquid.

   The block never changes a price. It drives the quantity the product form
   will submit; the discount itself is a Shopify automatic "Amount off
   products" discount keyed on a minimum quantity of items. Everything painted
   here is a preview of the cart, computed the way Shopify computes it —
   percentage off the line total, rounded half up to the cent — so the PDP and
   the cart agree to the penny.

   Two writes per selection, deliberately. Dispatching change on the theme's
   own [data-quantity-input] is the path theme.js already listens on, but the
   stepper block is optional in the theme editor, so the form's [name=quantity]
   is written directly as well — that input is what cart.addItem() serialises.
   ========================================================================== */
(function () {
  'use strict';

  // The snippet ships its own <script> tag, so a page carrying two product
  // sections loads this file twice. The document listeners below must bind
  // once; initAll() already walks every widget on the page, so one execution
  // serves them all.
  if (window.quantityBreaksInitialized) return;
  window.quantityBreaksInitialized = true;

  var WIDGET = '[data-quantity-breaks]';
  var TIER = '[data-quantity-break-tier]';
  var RADIO = 'quantity-breaks__radio';

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function find(scope, selector) {
    return scope ? scope.querySelector(selector) : null;
  }

  function findAll(scope, selector) {
    return scope ? toArray(scope.querySelectorAll(selector)) : [];
  }

  /* ---------------------------------------------------------------- money */

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number === null) return '0';

    var parts = (number / 100.0).toFixed(precision).split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  // Follows formatMoney in theme.js so the block and the rest of the PDP agree
  // on how a price is spelled. Not a straight copy: theme.js handles only the
  // four comma/no-decimal formats and has no default branch, so it renders an
  // empty number for the space- and apostrophe-separator formats. Those three
  // are handled below, along with a default, which costs nothing here and means
  // this block is not the thing that breaks if the shop's money_format changes.
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ------------------------------------------------------------------ dom */

  function scopeOf(widget) {
    return widget.closest('[data-section-id]') || widget.closest('.quick-product') || document;
  }

  function formOf(widget) {
    return find(scopeOf(widget), 'form[data-product-form]');
  }

  function stepperOf(widget) {
    return find(scopeOf(widget), '[data-quantity-input]');
  }

  function tiersOf(widget) {
    return findAll(widget, TIER);
  }

  // data-selected, not input.checked, is this widget's own record of the choice.
  // The radios live outside the <form>, so the browser groups them by name across
  // the whole document; reading .checked would make one widget's state depend on
  // every other widget's radio names. Writing it stays correct either way.
  function selectedTier(widget) {
    return find(widget, TIER + '[data-selected="true"]');
  }

  function variantPrices(widget) {
    var holder = find(widget, '[data-quantity-break-variants]');
    if (!holder) return {};

    try {
      return JSON.parse(holder.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  /* -------------------------------------------------------------- pricing */

  // Shopify rounds a percentage discount to the nearest cent on the line total.
  function savingFor(lineTotal, percent) {
    return Math.round((lineTotal * percent) / 100);
  }

  function renderPrices(widget) {
    var unitPrice = parseInt(widget.getAttribute('data-unit-price'), 10);
    if (isNaN(unitPrice)) return;

    tiersOf(widget).forEach(function (tier) {
      var quantity = parseInt(tier.getAttribute('data-quantity'), 10);
      var percent = parseFloat(tier.getAttribute('data-discount')) || 0;
      if (isNaN(quantity) || quantity < 1) return;

      var regular = unitPrice * quantity;
      var total = regular - savingFor(regular, percent);

      // Only the tier total is a round figure; the per-unit price is floored
      // the way the Liquid floors it, so the two renderings of the same card
      // never differ by a cent across a variant change.
      var totalEl = find(tier, '[data-quantity-break-total]');
      if (totalEl) totalEl.textContent = formatMoney(total);

      var unitCompareEl = find(tier, '[data-quantity-break-unit-compare]');
      if (unitCompareEl) unitCompareEl.textContent = formatMoney(unitPrice);

      var unitEl = find(tier, '[data-quantity-break-unit-price]');
      if (unitEl) unitEl.textContent = formatMoney(Math.floor(total / quantity));
    });

    renderSavings(widget);
    // The block's own button can carry [total]; a variant change moves that
    // figure, so it has to be repainted here and not only on a tier change.
    syncButtonLabel(widget);
    syncCartTotal(widget);
  }

  // What the shopper is committing to at the current selection. Recomputed
  // rather than read off the card, because the cards can be re-rendered by a
  // variant change between one call and the next.
  function selectedTotal(widget) {
    var unitPrice = parseInt(widget.getAttribute('data-unit-price'), 10);
    if (isNaN(unitPrice)) return null;

    var tier = selectedTier(widget);
    var percent = tier ? parseFloat(tier.getAttribute('data-discount')) || 0 : 0;
    var regular = unitPrice * selectedQuantity(widget);

    return regular - savingFor(regular, percent);
  }

  // Puts the running total on the theme's own add-to-cart button, so the price
  // being committed to sits on the control that commits to it.
  //
  // Its own element appended after the label, never part of the label: theme.js
  // rewrites [data-add-to-cart-text] wholesale on every variant change, and a
  // total folded into that string would be erased by the next one. For the same
  // reason this is re-run from the variant-change handler and from the
  // subscription poll rather than written once at init.
  //
  // aria-hidden because the button carries an aria-label, which would otherwise
  // silently drop the price from the accessible name anyway; the same figures
  // are already announced from the tier cards and the savings line.
  function syncCartTotal(widget) {
    if (widget.getAttribute('data-cart-total') !== 'true') return;

    var scope = scopeOf(widget);
    if (scope === document) return;

    // Hidden means a subscription is selected, and the tiers no longer describe
    // what the line will cost.
    var total = widget.hidden ? null : selectedTotal(widget);

    findAll(scope, '[data-add-to-cart]').forEach(function (button) {
      var el = find(button, '[data-quantity-break-cart-total]');

      if (total === null || button.disabled) {
        if (el) el.textContent = '';
        return;
      }

      if (!el) {
        el = document.createElement('span');
        el.className = 'quantity-breaks__cart-total';
        el.setAttribute('data-quantity-break-cart-total', '');
        el.setAttribute('aria-hidden', 'true');

        var label = find(button, '[data-add-to-cart-text]');
        if (label && label.parentNode === button) button.insertBefore(el, label.nextSibling);
        else button.appendChild(el);
      }

      // The gap is a margin, not a leading space: HTML collapses whitespace at
      // the start of an inline element that follows text.
      //
      // Compared before writing: this runs on a 500ms poll, and an unconditional
      // textContent write is a style recalculation twice a second for the life
      // of the page. The element is re-found every call, so a button theme.js
      // has replaced still gets its total back.
      var text = '\u00b7 ' + formatMoney(total);
      if (el.textContent !== text) el.textContent = text;
    });
  }

  function renderSavings(widget) {
    var savingsEl = find(widget, '[data-quantity-break-savings]');
    if (!savingsEl) return;

    var template = widget.getAttribute('data-savings-label') || '';
    var tier = selectedTier(widget);
    var unitPrice = parseInt(widget.getAttribute('data-unit-price'), 10);
    var saving = 0;

    if (template && tier && !isNaN(unitPrice)) {
      var quantity = parseInt(tier.getAttribute('data-quantity'), 10);
      var percent = parseFloat(tier.getAttribute('data-discount')) || 0;
      saving = savingFor(unitPrice * quantity, percent);
    }

    if (saving > 0) {
      savingsEl.textContent = template.replace('[amount]', formatMoney(saving));
      savingsEl.hidden = false;
    } else {
      savingsEl.textContent = '';
      savingsEl.hidden = true;
    }
  }

  /* ------------------------------------------------------------- quantity */

  function pushQuantity(widget, quantity) {
    var stepper = stepperOf(widget);
    var form = formOf(widget);

    // Guards the stepper listener below against reacting to our own write.
    widget.setAttribute('data-syncing', 'true');

    if (stepper) {
      stepper.value = String(quantity);
      stepper.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (form) {
      findAll(form, '[name="quantity"]').forEach(function (input) {
        input.value = String(quantity);
      });
    }

    widget.removeAttribute('data-syncing');
  }

  function markSelection(widget, tierToSelect) {
    tiersOf(widget).forEach(function (tier) {
      var isSelected = tier === tierToSelect;
      var input = find(tier, 'input');

      tier.setAttribute('data-selected', String(isSelected));
      if (input) input.checked = isSelected;
    });

    renderSavings(widget);
    syncButtonLabel(widget);
    syncCartTotal(widget);
  }

  // Stepper -> tiers. An exact match wins; otherwise fall back to the richest
  // tier the shopper has already qualified for, so typing 5 still reads as the
  // "buy 3" tier rather than clearing the whole widget.
  function reflectQuantity(widget, quantity) {
    var tiers = tiersOf(widget);
    var best = null;

    tiers.forEach(function (tier) {
      var tierQuantity = parseInt(tier.getAttribute('data-quantity'), 10);
      if (isNaN(tierQuantity) || tierQuantity > quantity) return;
      if (!best || tierQuantity > parseInt(best.getAttribute('data-quantity'), 10)) best = tier;
    });

    markSelection(widget, best);
  }

  /* --------------------------------------------------------- own submit */

  // A bundle app (Kaching on this store) binds to the theme's add-to-cart
  // button and adds its own selection, ignoring the form's quantity field -
  // which is the field the tiers drive. Detect a rendered bundle widget and,
  // when there is one, give the block its own button straight to /cart/add.js.
  function bundleWidgetPresent() {
    var els = findAll(document, 'kaching-bundles-block, kaching-bundle, [data-kaching-bundles-block]');

    for (var i = 0; i < els.length; i++) {
      // The app embed loads on every page; only a widget that actually rendered
      // and occupies space is competing for the button.
      if (els[i].offsetParent !== null && els[i].getBoundingClientRect().height > 0) return true;
    }

    return false;
  }

  function setError(widget, message) {
    var el = find(widget, '[data-quantity-break-error]');
    if (!el) return;
    // Unhidden first: role="alert" only announces a change made to a node that
    // is already rendered.
    el.hidden = !message;
    el.textContent = message || '';
  }

  // What this button would add. Normally the selected tier, but reflectQuantity
  // deselects every tier when the stepper holds less than the smallest one - a
  // merchant may disable tier 1 or start the tiers at 2 - and the button must
  // still add something rather than sit there dead.
  function selectedQuantity(widget) {
    var tier = selectedTier(widget);
    if (tier) return parseInt(tier.getAttribute('data-quantity'), 10) || 1;

    var stepper = stepperOf(widget);
    if (stepper) return parseInt(stepper.value, 10) || 1;

    return 1;
  }

  function syncButtonLabel(widget) {
    var button = find(widget, '[data-quantity-break-add]');
    if (!button) return;

    var label = button.getAttribute('data-label') || '';
    var text = find(button, '[data-quantity-break-add-text]');
    if (!text || !label) return;

    var total = selectedTotal(widget);

    text.textContent = label
      .replace('[quantity]', selectedQuantity(widget))
      .replace('[total]', total === null ? '' : formatMoney(total));
  }

  function submitTier(widget, button) {
    // A second tap while the first request is still open would add the tier
    // twice. aria-busy is the flag the CSS already reads, so it is the flag
    // checked here rather than a second one.
    if (button.getAttribute('aria-busy') === 'true') return;

    var form = formOf(widget);
    if (!form) return;

    var idInput = find(form, '[name="id"]');
    var variantId = idInput && idInput.value;
    if (!variantId) { setError(widget, widget.getAttribute('data-choose-error')); return; }

    var quantity = selectedQuantity(widget);

    // Build the payload explicitly rather than serialising the form, so a
    // bundle app's own hidden inputs cannot ride along.
    var body = 'id=' + encodeURIComponent(variantId) + '&quantity=' + quantity;
    findAll(form, '[name^="properties"]').forEach(function (input) {
      if (input.type === 'checkbox' && !input.checked) return;
      body += '&' + encodeURIComponent(input.name) + '=' + encodeURIComponent(input.value);
    });

    setError(widget, '');
    button.setAttribute('aria-busy', 'true');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body: body
    }).then(function (response) {
      return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    }).then(function (result) {
      button.removeAttribute('aria-busy');

      if (!result.ok) {
        // Shopify's own description is already in the shopper's language.
        setError(widget, (result.data && result.data.description) || widget.getAttribute('data-add-error'));
        return;
      }

      // The theme refreshes its cart count off this document event, then the
      // header icon is what opens its drawer.
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      var icon = find(document, '[data-js-cart-icon]');
      if (icon) icon.click();
    })['catch'](function () {
      button.removeAttribute('aria-busy');
      setError(widget, widget.getAttribute('data-add-error'));
    });
  }

  function refreshSubmit(widget) {
    var wrap = find(widget, '[data-quantity-break-submit]');
    if (!wrap) return false;

    var mode = widget.getAttribute('data-add-to-cart-mode') || 'auto';
    var show = mode === 'always' || (mode === 'auto' && bundleWidgetPresent());

    wrap.hidden = !show;
    if (show) syncButtonLabel(widget);

    return show;
  }

  /* ------------------------------------------------------ subscriptions */

  // Subscription apps write the chosen plan into the form's selling_plan field
  // (Seal on this store). When a plan is chosen the line is a subscription, and
  // a discount set to One-time purchase will not touch it - so the tiers would
  // be advertising a saving that never arrives. Hide them and give the shopper
  // the stock quantity stepper back.
  function subscriptionSelected(widget) {
    var form = formOf(widget);
    if (!form) return false;

    var plan = find(form, '[name="selling_plan"]');
    return !!(plan && plan.value);
  }

  // [hidden] is display:none !important, which no transition can touch, so it
  // can no longer be what drives the change - it is applied at the end of a
  // collapse and lifted at the start of an expand instead. max-height is
  // measured rather than guessed: a fixed ceiling would clip a block taller
  // than the guess, and ease down from nothing for a block shorter than it.
  function collapse(widget, hidden) {
    if (widget.quantityBreaksCollapse) widget.quantityBreaksCollapse(true);

    widget.classList.add('is-collapsing');

    var target;

    if (hidden) {
      // Open now, so its own height is where the collapse starts from.
      widget.style.maxHeight = widget.scrollHeight + 'px';
      target = 0;
    } else {
      // Coming back from [hidden], i.e. from display:none, where there is no
      // previous value for either property to interpolate from - so the
      // collapsed state has to be put on inline and painted for a frame first.
      // Clearing opacity then hands it back to the stylesheet's 1 to animate
      // towards; max-height is driven to the measured height below.
      widget.hidden = false;
      widget.style.maxHeight = '0px';
      widget.style.opacity = '0';
      void widget.offsetHeight;
      widget.style.opacity = '';
      // scrollHeight still reports the full content while max-height is 0.
      target = widget.scrollHeight;
    }

    var finish = function (cancelled) {
      if (!widget.quantityBreaksCollapse) return;
      clearTimeout(widget.quantityBreaksCollapseTimer);
      widget.removeEventListener('transitionend', onEnd);
      widget.quantityBreaksCollapse = null;
      widget.classList.remove('is-collapsing');
      widget.style.maxHeight = '';
      widget.style.opacity = '';
      // On cancel the next call takes over and will set this itself.
      if (!cancelled) widget.hidden = widget.getAttribute('data-subscription-hidden') === 'true';
    };

    var onEnd = function (event) {
      // Only the block's own height settling counts. A tier card finishing its
      // border-colour fade bubbles a transitionend through here too.
      if (event.target !== widget || event.propertyName !== 'max-height') return;
      finish(false);
    };

    widget.quantityBreaksCollapse = finish;
    widget.addEventListener('transitionend', onEnd);

    // A zero-length transition - prefers-reduced-motion, or a browser that
    // skips it - fires no transitionend, so the end state cannot wait on one.
    widget.quantityBreaksCollapseTimer = setTimeout(function () { finish(false); }, 420);

    // Next frame, so the starting height is painted before the target lands.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (!widget.quantityBreaksCollapse) return;
        widget.style.maxHeight = target + 'px';
      });
    });
  }

  function refreshSubscriptionState(widget) {
    if (widget.getAttribute('data-hide-on-subscription') !== 'true') return;

    // The subscription app renders its own markup, so [name="selling_plan"] does
    // not exist for the first second or two of a page load. Until it does, the
    // real state is simply unknown and every correction is the app reporting in
    // for the first time, not the shopper changing their mind - so it is
    // applied outright. Animating those plays a slow collapse a second or two
    // after the block has already been sitting there, which is the flash this
    // guards against. Only once the field exists does a change mean a click.
    var form = formOf(widget);
    var planField = form ? find(form, '[name="selling_plan"]') : null;
    var hidden = !!(planField && planField.value);
    var settled = widget.getAttribute('data-subscription-settled') === 'true';

    if (planField) widget.setAttribute('data-subscription-settled', 'true');
    if (String(hidden) === widget.getAttribute('data-subscription-hidden')) return;
    widget.setAttribute('data-subscription-hidden', String(hidden));

    if (settled) {
      collapse(widget, hidden);
    } else {
      widget.hidden = hidden;
    }

    var scope = scopeOf(widget);
    var takeover = widget.getAttribute('data-takeover-quantity') === 'true';

    if (hidden) {
      // Hand the quantity back to the theme's stepper, starting at one.
      if (takeover && scope !== document) scope.classList.remove('has-quantity-breaks');
      pushQuantity(widget, 1);
    } else {
      if (takeover && scope !== document) scope.classList.add('has-quantity-breaks');
      var tier = selectedTier(widget);
      if (tier) pushQuantity(widget, parseInt(tier.getAttribute('data-quantity'), 10) || 1);
    }

    syncCartTotal(widget);
  }

  /* ----------------------------------------------------------------- init */

  function init(widget) {
    if (widget.getAttribute('data-quantity-breaks-ready') === 'true') return;
    widget.setAttribute('data-quantity-breaks-ready', 'true');

    if (widget.getAttribute('data-takeover-quantity') === 'true') {
      var scope = scopeOf(widget);
      if (scope !== document) scope.classList.add('has-quantity-breaks');
    }

    var stepper = stepperOf(widget);
    if (stepper) {
      stepper.addEventListener('change', function () {
        if (widget.getAttribute('data-syncing') === 'true') return;
        reflectQuantity(widget, parseInt(stepper.value, 10) || 1);
      });
    }

    // The 500ms poll below stays the backstop, because the subscription app
    // writes selling_plan by property and fires nothing. The plan radio the
    // shopper actually clicks does fire a change that bubbles to the form, so
    // listen for that too and start the collapse on the same frame as the click
    // rather than up to half a second later. refreshSubscriptionState
    // early-returns when nothing moved, so the extra calls cost nothing.
    var planForm = formOf(widget);
    if (planForm) {
      planForm.addEventListener('change', function () {
        refreshSubscriptionState(widget);
      });
    }

    // Re-assert the server-rendered choice rather than trusting the radios, which
    // a same-named group elsewhere on the page may already have cleared.
    var tier = selectedTier(widget) || tiersOf(widget)[0] || null;
    if (tier) markSelection(widget, tier);

    renderPrices(widget);

    if (tier) pushQuantity(widget, parseInt(tier.getAttribute('data-quantity'), 10) || 1);

    // Before the bundle-app checks below, which return early: selling_plan is a
    // hidden field a subscription app writes by property, so it changes no
    // attribute and fires no event and has to be watched.
    refreshSubscriptionState(widget);
    // Held so shopify:section:unload can stop it. Without that, every reload of
    // the section in the theme editor leaves another timer running against a
    // widget that is no longer in the document.
    widget.quantityBreaksPoll = setInterval(function () {
      refreshSubscriptionState(widget);
      // Cheap and idempotent: the total is only rewritten when the button has
      // lost it, which is how it survives theme.js replacing the form markup.
      syncCartTotal(widget);
    }, 500);

    // The bundle app renders asynchronously. Only 'auto' has anything to wait
    // for; the other two modes are settled by the first call. Stop the moment
    // the app is found, rather than after a fixed number of tries - missing it
    // leaves the theme's button, which the app owns, adding the wrong quantity.
    if (refreshSubmit(widget)) return;
    if (widget.getAttribute('data-add-to-cart-mode') !== 'auto') return;
    if (!find(widget, '[data-quantity-break-submit]')) return;

    var tries = 0;
    var poll = setInterval(function () {
      if (refreshSubmit(widget) || ++tries >= 30) clearInterval(poll);
    }, 700);
  }

  function initAll(root) {
    findAll(root || document, WIDGET).forEach(init);
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.classList || !input.classList.contains(RADIO)) return;

    var widget = input.closest(WIDGET);
    var tier = input.closest(TIER);
    if (!widget || !tier) return;

    markSelection(widget, tier);
    pushQuantity(widget, parseInt(tier.getAttribute('data-quantity'), 10) || 1);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-quantity-break-add]');
    if (!button) return;

    var widget = button.closest(WIDGET);
    if (!widget) return;

    event.preventDefault();
    submitTier(widget, button);
  });

  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;

    findAll(document, WIDGET).forEach(function (widget) {
      var price = variantPrices(widget)[String(variant.id)];
      // A variant id belongs to exactly one product, so this both routes the
      // event to the right widget and skips widgets for other products.
      if (typeof price !== 'number') return;

      widget.setAttribute('data-unit-price', String(price));
      renderPrices(widget);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('quickview:loaded', function () { initAll(); });

  // Theme editor only: the section's markup is about to be thrown away, so the
  // subscription poll it started has nothing left to watch.
  document.addEventListener('shopify:section:unload', function (event) {
    findAll(event.target, WIDGET).forEach(function (widget) {
      if (widget.quantityBreaksPoll) clearInterval(widget.quantityBreaksPoll);
    });
  });
})();
